import React from 'react';
import { Box, Text, IconButton, Tooltip } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { LAUNCHER } from './launcherVisualTokens';
import { getToolIconTheme } from './toolIconTheme';

/**
 * Remaining tools (not in Favorites): navigate on click; add via hover-only control.
 */
const AvailableToolTile = ({ app, onOpen, canAdd, onAdd }) => {
  const { Icon, label, id } = app;
  const iconTheme = getToolIconTheme(id);

  return (
    <Box
      role="group"
      position="relative"
      w={LAUNCHER.appTileWidth}
      minW={LAUNCHER.appTileWidth}
      maxW={LAUNCHER.appTileWidth}
      flexShrink={0}
      sx={
        canAdd
          ? {
              '&:hover .avail-add-btn, &:focus-within .avail-add-btn': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }
          : undefined
      }
    >
      <Box
        as="button"
        type="button"
        onClick={() => onOpen(app)}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        gap={1.5}
        py={2}
        px={1.5}
        minH={LAUNCHER.appTileMinH}
        w="100%"
        borderRadius="lg"
        borderWidth={0}
        bg="transparent"
        cursor="pointer"
        transition="transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease"
        _hover={{
          bg: 'rgba(255, 255, 255, 0.05)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.3)',
          '& .avail-icon-well': {
            background: iconTheme.containerHover,
            color: iconTheme.iconColor,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.06)',
          },
        }}
        _active={{ transform: 'translateY(0)', boxShadow: 'none' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'rgba(45, 212, 191, 0.75)',
          outlineOffset: '2px',
        }}
        sx={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Box
          className="avail-icon-well"
          w={LAUNCHER.launcherIconWell}
          h={LAUNCHER.launcherIconWell}
          borderRadius="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={iconTheme.iconColor}
          transition="background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease"
          sx={{
            background: iconTheme.containerBg,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
          }}
        >
          <Icon size={LAUNCHER.launcherIconSize} aria-hidden />
        </Box>
        <Text
          as="span"
          {...LAUNCHER.tileAppLabel}
          noOfLines={2}
          w="100%"
          maxW={LAUNCHER.appTileWidth}
          mx="auto"
          px={0.5}
          overflowWrap="break-word"
          wordBreak="break-word"
        >
          {label}
        </Text>
      </Box>

      {canAdd ? (
        <Tooltip
          label="Add to Favorites"
          placement="top"
          hasArrow
          openDelay={220}
          shouldWrapChildren
          bg="gray.800"
          color="white"
        >
          <IconButton
            className="avail-add-btn"
            aria-label={`Add ${label} to Favorites`}
            icon={<FiPlus size={14} strokeWidth={2.5} />}
            size="xs"
            variant="outline"
            position="absolute"
            top={0}
            right={0}
            minW="24px"
            h="24px"
            borderRadius="full"
            borderColor="rgba(255, 255, 255, 0.14)"
            color="rgba(45, 212, 191, 0.95)"
            bg="rgba(32, 32, 32, 0.95)"
            opacity={0}
            transition="opacity 0.18s ease, border-color 0.18s ease, background 0.18s ease"
            pointerEvents="none"
            boxShadow="0 2px 10px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.06)"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAdd(id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            _hover={{
              bg: 'rgba(13, 148, 136, 0.22)',
              borderColor: 'rgba(45, 212, 191, 0.45)',
            }}
            _focusVisible={{ opacity: 1, pointerEvents: 'auto' }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default AvailableToolTile;
